/**
 * @file Cross-tab localStorage lock.
 *
 * Ensures only one browser tab calls the refresh endpoint at a time. The lock
 * value is an epoch-ms expiry timestamp — a built-in TTL so a crashed tab
 * cannot hold the lock indefinitely.
 */

import { isNullish } from '@/utils/null-utils';
import { TOKEN_REFRESH_CONFIG } from '../config';

const { lockKey: LOCK_KEY, lockTtlMs: LOCK_TTL_MS } = TOKEN_REFRESH_CONFIG;

/**
 * Attempts to acquire the cross-tab lock.
 * @returns `true` if this tab now owns the lock.
 */
export function acquireLock(): boolean {
  try {
    const now = Date.now();
    const existing = localStorage.getItem(LOCK_KEY);

    // Lock is held and not yet expired.
    const expiry = Number(existing);
    if (!isNullish(existing) && !Number.isNaN(expiry) && expiry > now) return false;

    localStorage.setItem(LOCK_KEY, String(now + LOCK_TTL_MS));
    return true;
  } catch (error) {
    // localStorage unavailable (Safari private mode, quota, etc.) — proceed
    // without cross-tab coordination; the SLAS rate-limit retry in the server
    // endpoint handles the worst case.
    console.warn('[token-refresh] localStorage unavailable for cross-tab lock:', error);
    return true;
  }
}

/** Releases the cross-tab lock. */
export function releaseLock(): void {
  try {
    localStorage.removeItem(LOCK_KEY);
  } catch {
    // Swallow — best-effort cleanup.
  }
}
